import { useState, useEffect, useCallback } from 'react';
import { fetchOffertStatus } from '../../../utils/offertStatusConfig';

/**
 * Custom hook för att hantera offert-data och relaterad information
 * 
 * @param {string} offertId - ID för offerten som ska hämtas
 * @returns {Object} - Offert-data och relaterade funktioner
 */
const useOffertData = (offertId) => {
  // State
  const [offert, setOffert] = useState(null);
  const [procurement, setProcurement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedbackThread, setFeedbackThread] = useState(null);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [intelligentStatus, setIntelligentStatus] = useState(null);

  // Hämta offert-data
  const fetchOffertData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/offert/${offertId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Kunde inte hämta offert');
      }

      const data = await response.json();
      
      if (data.success) {
        setOffert(data.data.offert);
        // Sätt procurement direkt från offert.procurementId (som är populerad)
        setProcurement(data.data.offert.procurementId);
      } else {
        throw new Error(data.message || 'Kunde inte hämta offert');
      }
    } catch (err) {
      console.error('❌ Fel vid hämtning av offert:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [offertId]);

  // Hämta feedback-tråd
  const fetchFeedbackThread = useCallback(async () => {
    if (!offert?._id) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/offert/${offert._id}/thread`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setFeedbackThread(data.data);
        }
      }
    } catch (err) {
      console.error('❌ Fel vid hämtning av feedback-tråd:', err);
    }
  }, [offert?._id]);

  // Hämta feedback-historik från alla versioner
  const fetchFeedbackHistory = useCallback(async () => {
    if (!offert?._id) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/offert/${offert._id}/feedback-history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setFeedbackHistory(data.data.history || []);
        }
      }
    } catch (err) {
      console.error('❌ Fel vid hämtning av feedback-historik:', err);
    }
  }, [offert?._id]);

  // Hämta intelligent status
  const fetchIntelligentStatus = useCallback(async () => {
    if (!offert?._id) return;
    
    try {
      const statusData = await fetchOffertStatus(offert._id);
      if (statusData) {
        setIntelligentStatus(statusData.status);
      }
    } catch (error) {
      console.error('❌ Fel vid hämtning av intelligent status:', error);
    }
  }, [offert?._id]);

  // Hämta offert-data vid mount
  useEffect(() => {
    fetchOffertData();
  }, [fetchOffertData]);

  // Hämta feedback-tråd när offert laddas
  useEffect(() => {
    fetchFeedbackThread();
  }, [fetchFeedbackThread]);

  // Hämta feedback-historik när offert laddas
  useEffect(() => {
    fetchFeedbackHistory();
  }, [fetchFeedbackHistory]);

  // Hämta intelligent status när offert laddas
  useEffect(() => {
    fetchIntelligentStatus();
  }, [fetchIntelligentStatus]);

  return {
    // Data
    offert,
    procurement,
    feedbackThread,
    feedbackHistory,
    intelligentStatus,
    loading,
    error,
    
    // Setters (för uppdateringar från andra hooks/komponenter)
    setOffert,
    setProcurement,
    setFeedbackThread,
    setFeedbackHistory,
    setIntelligentStatus,
    
    // Fetch functions
    fetchOffertData,
    fetchFeedbackThread,
    fetchFeedbackHistory,
    fetchIntelligentStatus,
    refetch: fetchOffertData, // Alias för enklare refetch
  };
};

export default useOffertData;

